import { useCallback, useState } from 'preact/hooks';
import { useDesignStore } from '../store/design.store';
import { CanvasPreview } from '../components/CanvasPreview';
import { StylePicker } from '../components/StylePicker';
import { ColorPicker } from '../components/ColorPicker';
import { FontPicker } from '../components/FontPicker';
import { saveDesign, listDesigns, deleteDesign } from '../db/local';
import type { StoredDesign, StylePreset } from '../types';

const MAX_TEXT_LENGTH = 10;

export default function Create() {
  const {
    current,
    setText,
    setPreset,
    setTextColor,
    setBackgroundColor,
    setSecondaryColor,
    setFontFamily,
    setTextOrientation,
    markSaved,
    loadDesign,
  } = useDesignStore();

  const [savedDesigns, setSavedDesigns] = useState<StoredDesign[]>([]);
  const [showSaved, setShowSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleTextInput = useCallback(
    (e: Event) => {
      const val = (e.target as HTMLInputElement).value;
      if (val.length <= MAX_TEXT_LENGTH) {
        setText(val);
      }
    },
    [setText],
  );

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const stored: StoredDesign = {
        ...current,
        _schemaVersion: 1,
      };
      await saveDesign(stored);
      markSaved();
    } finally {
      setSaving(false);
    }
  }, [current, markSaved]);

  const handleLoadList = useCallback(async () => {
    const designs = await listDesigns();
    setSavedDesigns(designs);
    setShowSaved(true);
  }, []);

  const handleDelete = useCallback(async (id: string) => {
    await deleteDesign(id);
    const designs = await listDesigns();
    setSavedDesigns(designs);
  }, []);

  return (
    <div class="page-create">
      <main class="create-layout">
        {/* Canvas preview */}
        <section class="canvas-section">
          <CanvasPreview design={current} className="uchiwa-canvas" />
        </section>

        {/* Controls */}
        <aside class="controls-section">
          {/* Text input */}
          <div class="control-group">
            <label>テキスト</label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                value={current.text}
                onInput={handleTextInput}
                maxLength={MAX_TEXT_LENGTH}
                placeholder="文字を入力..."
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '2px solid var(--color-border)',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  boxSizing: 'border-box',
                  fontFamily: 'inherit',
                }}
              />
              <span style={{
                position: 'absolute',
                right: '10px',
                bottom: '-18px',
                fontSize: '0.75rem',
                color: current.text.length >= MAX_TEXT_LENGTH ? 'var(--color-error)' : 'var(--color-text-secondary)',
              }}>
                {current.text.length}/{MAX_TEXT_LENGTH}
              </span>
            </div>
          </div>

          {/* Orientation toggle */}
          <div class="control-group">
            <label>文字方向</label>
            <div style={{ display: 'flex', gap: '4px' }}>
              {(['vertical', 'horizontal'] as const).map((dir) => (
                <button
                  key={dir}
                  onClick={() => setTextOrientation(dir)}
                  style={{
                    flex: 1,
                    padding: '8px',
                    border: current.style.textOrientation === dir
                      ? '2px solid var(--color-primary)'
                      : '2px solid var(--color-border)',
                    borderRadius: '8px',
                    background: current.style.textOrientation === dir ? 'var(--color-surface)' : 'white',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    fontWeight: current.style.textOrientation === dir ? 'bold' : 'normal',
                  }}
                >
                  {dir === 'vertical' ? '↕ 縦書き' : '↔ 横書き'}
                </button>
              ))}
            </div>
          </div>

          {/* Style picker */}
          <div class="control-group">
            <label>スタイル</label>
            <StylePicker
              value={current.style.preset}
              onChange={(p: StylePreset) => setPreset(p)}
            />
          </div>

          {/* Font picker */}
          <div class="control-group">
            <FontPicker
              value={current.style.fontFamily}
              onChange={setFontFamily}
            />
          </div>

          {/* Colors */}
          <div class="control-group">
            <ColorPicker
              label="文字色"
              value={current.style.textColor}
              onChange={setTextColor}
            />
          </div>

          <div class="control-group">
            <ColorPicker
              label="背景色"
              value={current.style.backgroundColor}
              onChange={setBackgroundColor}
              presets={[
                '#FFFFFF', '#FFF5F8', '#F0F4FF', '#F0FFF4',
                '#FFF8E1', '#1A1A2E', '#000000', '#2D1B69',
                '#0D1B2A', '#FFEAA7', '#DFE6E9', '#FAD0C4',
              ]}
            />
          </div>

          <div class="control-group">
            <ColorPicker
              label="サブカラー"
              value={current.style.secondaryColor}
              onChange={setSecondaryColor}
            />
          </div>

          {/* Save / Load */}
          <div class="control-group" style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                flex: 1,
                padding: '10px',
                background: 'var(--color-primary)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontWeight: 'bold',
                cursor: 'pointer',
                fontSize: '0.9rem',
              }}
            >
              {saving ? '保存中...' : '💾 保存'}
            </button>
            <button
              onClick={handleLoadList}
              style={{
                flex: 1,
                padding: '10px',
                background: 'white',
                color: 'var(--color-text)',
                border: '2px solid var(--color-border)',
                borderRadius: '8px',
                fontWeight: 'bold',
                cursor: 'pointer',
                fontSize: '0.9rem',
              }}
            >
              📂 一覧
            </button>
          </div>
        </aside>
      </main>

      {/* Saved designs modal */}
      {showSaved && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 200,
          }}
          onClick={() => setShowSaved(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '16px',
              padding: '24px',
              maxWidth: '400px',
              width: '90%',
              maxHeight: '70vh',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px' }}>保存済みデザイン</h3>
            {savedDesigns.length === 0 ? (
              <p style={{ color: 'var(--color-text-secondary)' }}>保存済みデザインはありません</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {savedDesigns.map((d) => (
                  <div
                    key={d.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px',
                      border: '1px solid var(--color-border)',
                      borderRadius: '8px',
                    }}
                  >
                    <button
                      onClick={() => { loadDesign(d); setShowSaved(false); }}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        textAlign: 'left',
                        flex: 1,
                      }}
                    >
                      <div style={{ fontWeight: 'bold' }}>{d.text || '（空）'}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
                        {d.style.preset} · {new Date(d.updatedAt).toLocaleDateString('ja-JP')}
                      </div>
                    </button>
                    <button
                      onClick={() => handleDelete(d.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: 'var(--color-error)',
                        fontSize: '1.2rem',
                        padding: '4px 8px',
                      }}
                    >
                      🗑
                    </button>
                  </div>
                ))}
              </div>
            )}
            <button
              onClick={() => setShowSaved(false)}
              style={{
                marginTop: '16px',
                width: '100%',
                padding: '10px',
                background: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 'bold',
              }}
            >
              閉じる
            </button>
          </div>
        </div>
      )}

      <style>{`
        .page-create {
          min-height: calc(100vh - 52px);
          display: flex;
          flex-direction: column;
          background: var(--color-bg);
        }
        .create-layout {
          display: flex;
          flex: 1;
          gap: 0;
        }
        .canvas-section {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f0f0f0;
          padding: 24px;
          min-height: 300px;
        }
        .uchiwa-canvas {
          max-width: 100%;
          max-height: 60vh;
        }
        .controls-section {
          width: 300px;
          min-width: 280px;
          padding: 20px;
          border-left: 1px solid var(--color-border);
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          gap: 20px;
          background: white;
        }
        .control-group label {
          display: block;
          font-size: 0.85rem;
          font-weight: bold;
          color: var(--color-text-secondary);
          margin-bottom: 6px;
        }

        /* Mobile: stack layout */
        @media (max-width: 768px) {
          .create-layout {
            flex-direction: column;
          }
          .canvas-section {
            min-height: 250px;
            padding: 16px;
          }
          .uchiwa-canvas {
            max-height: 40vh;
          }
          .controls-section {
            width: 100%;
            min-width: auto;
            border-left: none;
            border-top: 1px solid var(--color-border);
            padding: 16px;
          }
        }
      `}</style>
    </div>
  );
}
